import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import LoadThemeComponent from '../../../base/LoadThemeComponent';
import { isKenoType } from '../../../utils/LotteryHelpers';
import { LineNumbers } from '../Types/Types';
import { getValidLinesAndStakes, shouldUseLineBuyPrice } from '../helpers/linesHelper';
import { getStakesSum } from '../helpers/pickHelper';
import useLinesHelper from '../helpers/useLinesHelper';
import { RootState } from '../redux/store';
import * as action from '../redux/storeSlice';

import { convertAmount, formatCurrency, getFractionDigits } from 'utils/formatters';
import { calculateGrossValue } from 'utils/tax';
import { isPremierDubai, isThankYouLotto } from 'utils/template';
import PurchasePromoCode from '../../promo-codes/PurchasePromoCode';
import { useUserBalance } from '../../../Store/Slicers/UserSlice';

function Purchase() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { numbersToPlay } = useLinesHelper();
    const {
        lines,
        multiplier,
        numberOfDraws,
        stakes,
        price,
        stakeMin,
        stakeMax,
        currencyCode,
        ticketPrice,
        ticketPriceTax,
        discountValue,
        lottery,
        stakeIsValid,
        purchaseButtonDisabled,
    } = useSelector((state: RootState) => state.playStore);
    const numbersPerLine = isKenoType(lottery.type) ? [numbersToPlay] : lottery.lotteryRule.numbers_per_line;
    const submitButton = useRef<HTMLButtonElement>();
    const setPurchaseButtonDisabled = (disabled: boolean) => {
        dispatch(action.setPurchaseButtonDisabled(disabled));
    };
    const isTemplateThankYouLotto = useMemo(() => isThankYouLotto(), []);
    const { balance } = useUserBalance();

    function getTicketPrice() {
        const oldPrice = convertAmount(ticketPrice, currencyCode);

        if (discountValue != null) {
            const newPrice = convertAmount(Math.max(0, discountValue), currencyCode);

            if (oldPrice !== newPrice) {
                return (
                    <>
                        <s className="old-price">{oldPrice}</s> {newPrice}
                    </>
                );
            }
        }

        return oldPrice;
    }

    function getTicketBuyPriceInLotteryCurrency() {
        const lotteryBuyPrice = lottery.lotteryRule.line_buy_price;
        const validResults = getValidLinesAndStakes(lottery, lines, stakes, numbersPerLine, stakeMin, stakeMax);
        const stakesSum = getStakesSum(validResults.validStakes);
        const basePrice = lotteryBuyPrice * numberOfDraws * stakesSum;
        const lotteryBuyCurrencyCode = lottery.lotteryRule.line_buy_currency_code;
        const formattedBasePrice = formatCurrency(basePrice, getFractionDigits(), lotteryBuyCurrencyCode);

        if (discountValue == null) {
            return formattedBasePrice;
        }
        let finalPrice = 0;
        if (discountValue > 0) {
            finalPrice = basePrice * (discountValue / ticketPrice);
        }

        const formattedFinalPrice = formatCurrency(finalPrice, getFractionDigits(), lotteryBuyCurrencyCode);

        if (formattedBasePrice !== formattedFinalPrice) {
            return (
                <>
                    <s className="old-price">{formattedBasePrice}</s> {formattedFinalPrice}
                </>
            );
        }

        return formattedBasePrice;
    }

    useEffect(calculateTicketPrice, [multiplier, numberOfDraws, stakes, lines, stakeIsValid]);

    function validateLinesLessThanMinLinePerTicket(validLines: LineNumbers[]) {
        return validLines.length < lottery.minLinesPerTicket;
    }

    // NOTE: Promo codes are calculated in PromoCode component
    function calculateTicketPrice() {
        // for stakes
        const newLinePriceGross = calculateGrossValue(price.priceNet, price.saleTaxPercent, price.saleTaxFixed);
        const validResults = getValidLinesAndStakes(lottery, lines, stakes, numbersPerLine, stakeMin, stakeMax);
        const stakesSum = getStakesSum(validResults.validStakes);
        const priceNet = price.priceNet * multiplier * numberOfDraws * stakesSum;
        const priceGross = calculateGrossValue(priceNet, price.saleTaxPercent, price.saleTaxFixed);
        const priceTaxSum = priceGross - priceNet;
        dispatch(action.setTicketPrice(priceGross));
        setPurchaseButtonDisabled(
            !stakeIsValid ||
                priceGross <= 0 ||
                validateLinesLessThanMinLinePerTicket(validResults.validLines) ||
                !isTicketPriceWithinLimits(priceGross)
        );
        dispatch(action.setLinePriceGross(newLinePriceGross));
        dispatch(action.setTicketPriceTax(priceTaxSum));
    }

    function isTicketPriceWithinLimits(priceGross: number): boolean {
        const minPrice = lottery.minTicketPrice;
        const maxPrice = lottery.maxTicketPrice;

        if (minPrice !== null && minPrice !== undefined && priceGross < minPrice) {
            return false;
        }

        if (maxPrice !== null && maxPrice !== undefined && priceGross > maxPrice) {
            return false;
        }

        return true;
    }

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.code === 'Enter' || event.code === 'NumpadEnter') {
                event.preventDefault();
            }
        };

        const handleFormSubmit = (event: SubmitEvent) => {
            // eslint-disable-next-line dot-notation
            if (!window['checkPromoCodeField']()) {
                event.preventDefault();
                setPurchaseButtonDisabled(false);

                return false;
            }

            setPurchaseButtonDisabled(true);

            return true;
        };

        document.addEventListener('keydown', handleKeyDown);
        submitButton.current?.form.addEventListener('submit', handleFormSubmit);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            submitButton.current?.form.removeEventListener('submit', handleFormSubmit);
        };
    }, []);

    function buyLabel(): string {
        if (isPremierDubai()) {
            return t('playPicker.confirm');
        }

        return t('playPicker.buyTicket');
    }

    const baseView = () => {
        if (isTemplateThankYouLotto) {
            return (
                <>
                    <div className="mt-2">
                        <div>
                            Total price:{' '}
                            <span className="fw-bold fs-5" id="purchaseSum" data-sum={ticketPrice}>
                                {getTicketPrice()}
                            </span>
                        </div>
                        <div>
                            Available Balance: <span className="fw-bold">{balance.formatted}</span>
                        </div>
                    </div>

                    <div className="mt-4">
                        <PurchasePromoCode />
                    </div>

                    {/* TODO: this have incorrect font size */}
                    <button type="submit" id="paymentSubmit" className="btn btn-primary btn-lg rounded-4 px-4 mt-4">
                        BUY NOW
                    </button>
                </>
            );
        }

        return (
            <div className="col d-sm-flex justify-content-sm-start justify-content-md-end justify-content-center btn-purchase-wrapper user-guide-play-buy">
                <button
                    ref={submitButton}
                    type="submit"
                    id="paymentSubmit"
                    disabled={purchaseButtonDisabled}
                    className={`btn btn-primary btn-submit btn-purchase d-block ${
                        purchaseButtonDisabled ? 'disabled' : ''
                    }`}
                >
                    {buyLabel()}
                    {shouldUseLineBuyPrice(lottery.currencyCode) && lottery.slug.includes('borlette')
                        ? getTicketBuyPriceInLotteryCurrency()
                        : ''}{' '}
                    (
                    <span id="purchaseSum" data-sum={ticketPrice}>
                        {getTicketPrice()}
                    </span>
                    )
                    {ticketPriceTax > 0 && (
                        <small id="purchaseTaxSum" className="tax-sum-info">
                            {convertAmount(ticketPriceTax, currencyCode)}
                        </small>
                    )}
                </button>
            </div>
        );
    };

    const defaultView = () => (
        <div className="col d-flex justify-content-center btn-purchase-wrapper user-guide-play-buy">
            <button
                ref={submitButton}
                type="submit"
                id="paymentSubmit"
                disabled={purchaseButtonDisabled}
                className={`btn btn-primary btn-submit btn-purchase d-block mx-auto ${
                    purchaseButtonDisabled ? 'disabled' : ''
                }`}
            >
                {buyLabel()}
                {shouldUseLineBuyPrice(lottery.currencyCode) && lottery.slug.includes('borlette')
                    ? getTicketBuyPriceInLotteryCurrency()
                    : ''}{' '}
                (
                <span id="purchaseSum" data-sum={ticketPrice}>
                    {getTicketPrice()}
                </span>
                )
                {ticketPriceTax > 0 && (
                    <small id="purchaseTaxSum" className="tax-sum-info">
                        {convertAmount(ticketPriceTax, currencyCode)}
                    </small>
                )}
            </button>
        </div>
    );

    return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
}

export default Purchase;
